import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { slugify } from "@utils/helpers";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaqItem } from "./faqItem";
import { useSlideEnterAnimation } from "@hooks/useSlideEnterAnimation";
import * as c from "./faqs.module.scss";

const Faqs = ({ className, heading = "Frequently Asked Questions", faqs, noAnchorLinks = false }) => {
  const [activeIndices, setActiveIndices] = useState(new Set());
  const [copiedStates, setCopiedStates] = useState({});
  const [, copyToClipboard] = useCopyToClipboard();

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the # symbol
      if (hash) {
        const faqIndex = faqs.findIndex((faq) => slugify(faq.question) === hash);
        if (faqIndex !== -1) {
          setActiveIndices((prev) => new Set(prev).add(faqIndex));
          setTimeout(() => {
            const element = document.getElementById(hash);
            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
          }, 100);
        }
      }
    };

    // Handle initial load
    handleHashChange();

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [faqs]);

  const toggleQuestion = (index) => {
    setActiveIndices((prevIndices) => {
      const newIndices = new Set(prevIndices);
      const wasOpen = newIndices.has(index);

      if (wasOpen) {
        newIndices.delete(index);
        // Only refresh ScrollTrigger when closing to recalculate positions
        setTimeout(() => {
          ScrollTrigger.refresh();
        }, 300); // Wait for CSS transition to complete
      } else {
        newIndices.add(index);
      }

      return newIndices;
    });
  };

  const handleCopy = (index, question) => {
    const questionId = slugify(question);
    const url = `${window.location.origin}${window.location.pathname}#${questionId}`;

    copyToClipboard(url);
    setCopiedStates((prev) => ({ ...prev, [index]: true }));

    // Open the FAQ when copying the link
    setActiveIndices((prev) => new Set(prev).add(index));

    setTimeout(() => {
      setCopiedStates((prev) => ({ ...prev, [index]: false }));
    }, 1500);
  };

  const isActive = (index) => activeIndices.has(index);
  const isCopied = (index) => copiedStates[index] || false;

  useSlideEnterAnimation({
    selector: "[data-faq-item]",
    dependencies: [faqs],
    shouldScale: false,
  });

  return (
    <div className={classNames(c.container, className)}>
      {heading && (
        <h4 className={classNames("h4", c.title)} data-faq-item>
          {heading}
        </h4>
      )}

      <div className={c.list}>
        {faqs.map((faq, index) => (
          <FaqItem
            key={index}
            faq={faq}
            index={index}
            isActive={isActive(index)}
            isCopied={isCopied(index)}
            onToggle={() => toggleQuestion(index)}
            onCopyLink={() => handleCopy(index, faq.question)}
            noAnchorLinks={noAnchorLinks}
            data-faq-item
          />
        ))}
      </div>
    </div>
  );
};

const getProcessedFaqs = (items) =>
  (items || []).map((faq) => ({
    ...faq,
    plainTextAnswer: faq.richTextAnswer?.raw ? documentToPlainTextString(JSON.parse(faq.richTextAnswer.raw)) : "",
  }));

export { Faqs, getProcessedFaqs };
